// Game progress sync for signed-in players on logicpuzzles.ca.
//
// Every web game here is a Flutter build served from this origin, and Flutter
// keeps its saved data in localStorage under `flutter.*` keys (the site's own
// Daily Crowns uses `crowns-puzzle-*`). Because the games share this origin,
// the site can mirror those keys into the account's cloud save
// (`spark_app_state`, app `web`, one row per key) without rebuilding a game:
// sign in on another computer and your levels, stars and streaks are there.
//
// Rules (per key, using the server row's `version` and a local hash of what we
// last reconciled):
//   - only here            -> upload (a guest's progress joins the account)
//   - only on the account  -> download
//   - changed here only    -> upload as is (so values may go down, e.g. hints)
//   - changed there only   -> download
//   - changed on both      -> merge: lists union, numbers max, booleans OR,
//                             objects key by key, ISO dates latest, else ours
// Deleted keys are never deleted remotely: progress only accumulates.
//
// While a game is running its in-memory copy is the truth, so syncs started
// then (`applyRemote: false`) upload but never overwrite local keys except to
// resolve a conflict. Downloads happen at page load, before a game starts.
//
// Pure apart from the `storage` and `server` it is handed, so it is tested in
// node (scripts/test-progress-sync.mjs) with fakes.

export const META_KEY = 'spark_progress_sync';
export const APP_ID = 'web';
const ROW_PREFIX = 'ls:';
const MAX_VALUE_CHARS = 100 * 1024;

const SYNC_KEY = /^(flutter\.|crowns-puzzle-)/;
// Per-device identity and plumbing: never copied between devices.
const DEVICE_KEY = /(device|install|uuid|guid|session|token|consent|analytics|firebase|fcm|remote_?config|notification|ad_?id|advert|tracking)/i;

export function isSyncKey(key) {
  return SYNC_KEY.test(key) && !DEVICE_KEY.test(key) && (ROW_PREFIX + key).length <= 128;
}

/** FNV-1a, enough to notice that a value changed. */
export function hash(str) {
  let h = 0x811c9dc5;
  for (let i = 0; i < str.length; i++) {
    h ^= str.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return (h >>> 0).toString(36);
}

const ISO_DATE = /^\d{4}-\d{2}-\d{2}([T ][\d:.]+(Z|[+-]\d{2}:?\d{2})?)?$/;
const isObj = (v) => v !== null && typeof v === 'object' && !Array.isArray(v);

function tryJson(str) {
  try {
    return { ok: true, value: JSON.parse(str) };
  } catch (e) {
    return { ok: false };
  }
}

/** Merges two decoded values; `a` (this device) wins anything ambiguous. */
export function mergeValues(a, b) {
  if (a === b) return a;
  if (typeof a === 'number' && typeof b === 'number') return Math.max(a, b);
  if (typeof a === 'boolean' && typeof b === 'boolean') return a || b;
  if (Array.isArray(a) && Array.isArray(b)) {
    const seen = new Set(a.map((x) => JSON.stringify(x)));
    return [...a, ...b.filter((x) => !seen.has(JSON.stringify(x)))];
  }
  if (isObj(a) && isObj(b)) {
    const out = { ...b };
    for (const k of Object.keys(a)) out[k] = k in b ? mergeValues(a[k], b[k]) : a[k];
    return out;
  }
  if (typeof a === 'string' && typeof b === 'string') {
    if (ISO_DATE.test(a) && ISO_DATE.test(b)) return a > b ? a : b;
    // Apps often keep a JSON document inside a string pref.
    const ja = tryJson(a);
    const jb = tryJson(b);
    if (ja.ok && jb.ok && typeof ja.value === 'object' && typeof jb.value === 'object' && ja.value && jb.value) {
      return JSON.stringify(mergeValues(ja.value, jb.value));
    }
  }
  return a;
}

/** Merges two raw localStorage strings (Flutter stores each value as JSON). */
export function mergeRaw(local, remote) {
  if (local === remote) return local;
  const l = tryJson(local);
  const r = tryJson(remote);
  if (!l.ok || !r.ok) return local;
  return JSON.stringify(mergeValues(l.value, r.value));
}

export function readMeta(storage) {
  try {
    const m = JSON.parse(storage.getItem(META_KEY) || 'null');
    if (m && typeof m === 'object' && m.keys) return m;
  } catch (e) { /* corrupt: start over */ }
  return { uid: null, keys: {}, tags: {} };
}

function writeMeta(storage, meta) {
  try {
    storage.setItem(META_KEY, JSON.stringify(meta));
  } catch (e) { /* storage full or blocked: next sync redoes the work */ }
}

/** The syncable keys in [storage] with their current values. */
export function localEntries(storage) {
  const out = new Map();
  for (let i = 0; i < storage.length; i++) {
    const k = storage.key(i);
    if (k && isSyncKey(k)) {
      const v = storage.getItem(k);
      if (v !== null && v.length <= MAX_VALUE_CHARS) out.set(k, v);
    }
  }
  return out;
}

/**
 * One sync pass.
 *   storage: localStorage-like (length, key, getItem, setItem)
 *   server:  { pull(): Promise<[{key, value, version, updated_at}]>,
 *              push(rows: [{key, value}]): Promise<[{key, version, updated_at}]> }
 *   uid:     the signed-in account
 *   applyRemote: false while a game is running (see the header); may be a
 *            function, read after the pull returns (a game can start meanwhile)
 *   game:    { id, name } for keys this device changed (e.g. 'crowns'); optional
 *   changedSince: Map key -> hash at the moment the game started; keys whose
 *            value differs are tagged with `game`
 * Returns { pulled, pushed, merged, rows } where rows are the account's rows
 * after the pass (for the progress summary).
 */
export async function syncOnce({ storage, server, uid, applyRemote = true, game = null, changedSince = null }) {
  const meta = readMeta(storage);
  const foreign = meta.uid && meta.uid !== uid; // another account used this browser before
  if (meta.uid !== uid) {
    meta.uid = uid;
    meta.keys = {};
  }
  meta.tags = meta.tags || {};
  meta.names = meta.names || {};

  const remoteRows = await server.pull();
  const apply = typeof applyRemote === 'function' ? !!applyRemote() : applyRemote;
  const remote = new Map();
  for (const row of remoteRows) {
    if (!row.key.startsWith(ROW_PREFIX) || typeof row.value?.v !== 'string') continue;
    remote.set(row.key.slice(ROW_PREFIX.length), row);
  }
  const local = localEntries(storage);

  if (game && changedSince) {
    meta.names[game.id] = game.name || meta.names[game.id];
    for (const [k, v] of local) {
      if (changedSince.get(k) !== hash(v)) meta.tags[k] = game.id;
    }
  }

  const uploads = [];
  let pulled = 0;
  let merged = 0;
  const keys = new Set([...local.keys(), ...remote.keys()]);
  for (const k of keys) {
    const L = local.has(k) ? local.get(k) : null;
    const S = remote.get(k);
    const m = meta.keys[k];
    if (S && S.value.g && !meta.tags[k]) meta.tags[k] = S.value.g;
    if (S && S.value.g && S.value.n && !meta.names[S.value.g]) meta.names[S.value.g] = S.value.n;

    if (S && L === null) {
      if (!apply) continue;
      storage.setItem(k, S.value.v);
      meta.keys[k] = { ver: S.version, h: hash(S.value.v) };
      pulled++;
    } else if (!S && L !== null) {
      // Don't give one account another's progress: remember what was here
      // when this account arrived and only upload it once it's played on.
      if (foreign) {
        meta.keys[k] = { ver: null, h: hash(L), foreign: true };
        continue;
      }
      if (m && m.foreign && m.h === hash(L)) continue;
      uploads.push({ k, v: L });
    } else if (S && L !== null) {
      const hL = hash(L);
      if (L === S.value.v) {
        meta.keys[k] = { ver: S.version, h: hL };
        continue;
      }
      const localChanged = !m || m.h !== hL;
      const remoteChanged = !m || m.ver !== S.version;
      if (foreign || (!localChanged && remoteChanged)) {
        if (!apply) continue;
        storage.setItem(k, S.value.v);
        meta.keys[k] = { ver: S.version, h: hash(S.value.v) };
        pulled++;
      } else if (localChanged && !remoteChanged) {
        uploads.push({ k, v: L });
      } else {
        const v = mergeRaw(L, S.value.v);
        merged++;
        if (v !== L) storage.setItem(k, v);
        if (v === S.value.v) meta.keys[k] = { ver: S.version, h: hash(v) };
        else uploads.push({ k, v });
      }
    }
  }

  let rows = remoteRows;
  if (uploads.length) {
    const result = await server.push(uploads.map(({ k, v }) => ({
      key: ROW_PREFIX + k,
      value: meta.tags[k]
        ? { v, g: meta.tags[k], ...(meta.names[meta.tags[k]] ? { n: meta.names[meta.tags[k]] } : {}) }
        : { v },
    })));
    const byKey = new Map(result.map((r) => [r.key, r]));
    for (const { k, v } of uploads) {
      const r = byKey.get(ROW_PREFIX + k);
      if (r) meta.keys[k] = { ver: r.version, h: hash(v) };
    }
    rows = [...remoteRows.filter((r) => !byKey.has(r.key)), ...result];
  }
  writeMeta(storage, meta);
  return { pulled, pushed: uploads.length, merged, rows };
}

/** Hashes of the syncable keys right now (taken when a game starts). */
export function snapshot(storage) {
  const out = new Map();
  for (const [k, v] of localEntries(storage)) out.set(k, hash(v));
  return out;
}

/**
 * What the account page shows: games with saved progress (by tag) and the
 * last time each was saved, newest first, plus the Daily Crowns streak.
 */
export function summarize(rows, storage) {
  const meta = readMeta(storage);
  const games = new Map();
  const names = { 'crowns-puzzle': 'Daily Crowns', ...(meta.names || {}) };
  for (const row of rows || []) {
    if (!row.key?.startsWith(ROW_PREFIX)) continue;
    const k = row.key.slice(ROW_PREFIX.length);
    const tag = row.value?.g || meta.tags?.[k] || (k.startsWith('crowns-puzzle-') ? 'crowns-puzzle' : null);
    if (!tag) continue;
    if (row.value?.n && !names[tag]) names[tag] = row.value.n;
    const t = Date.parse(row.updated_at) || 0;
    games.set(tag, Math.max(games.get(tag) || 0, t));
  }
  let streak = null;
  try {
    const s = JSON.parse(storage.getItem('crowns-puzzle-streak') || 'null');
    if (s && typeof s.count === 'number') streak = { count: s.count, best: s.best || s.count };
  } catch (e) { /* none */ }
  return {
    keys: (rows || []).filter((r) => r.key?.startsWith(ROW_PREFIX)).length,
    games: [...games.entries()]
      .map(([game, at]) => ({ game, name: names[game] || game, at }))
      .sort((a, b) => b.at - a.at),
    streak,
  };
}
